import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Leaf, Factory, Cog, Truck } from 'lucide-react';
import { servicesData, forestImage } from '../mock';
import { useLang } from '../i18n/LanguageContext';

const icons = { progettazione: Cog, imballaggi: Truck, installazione: Factory, prefabbricazione: Leaf };

const ActivityServicesSection = () => {
  const { t, lang } = useLang();
  const stats = t('stats');
  const sectors = t('sectors');

  return (
    <>
      <section id="attivita" className="relative py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div
              className="aspect-[4/5] w-full bg-cover bg-center shadow-xl"
              style={{ backgroundImage: `url('${forestImage}')` }}
            />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-[#0b2545] text-white p-8 w-72">
              <p className="text-xs tracking-[0.3em] uppercase text-[#f2b8a6]">{t('activity.sinceLabel')}</p>
              <p className="mt-2 text-lg leading-snug">{t('activity.sinceText')}</p>
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] text-[#c53114] font-semibold uppercase">{t('activity.eyebrow')}</p>
            <h2
              className="mt-2 text-3xl md:text-5xl font-semibold text-[#0b2545] leading-tight"
              style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
            >
              {t('activity.title')}
            </h2>
            <p className="mt-5 text-[#475569] leading-relaxed">{t('activity.body')}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {sectors.map((s) => (
                <div key={s} className="px-4 py-3 bg-[#f1f5f9] text-[#0b2545] text-sm font-medium border-l-2 border-[#c53114]">
                  {s}
                </div>
              ))}
            </div>

            <Link
              to="/attivita"
              className="mt-8 inline-flex items-center gap-2 bg-[#0b2545] text-white px-7 py-3 tracking-[0.2em] text-sm uppercase font-semibold hover:bg-[#c53114] transition-colors"
            >
              {t('activity.cta')} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0b2545] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-white">
              <div
                className="text-4xl md:text-5xl font-semibold text-[#f2b8a6]"
                style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
              >
                {s.value}
              </div>
              <p className="text-xs tracking-[0.25em] uppercase mt-2 text-white/70">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="servizi" className="py-24 bg-[#f5f7fa]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#c53114] font-semibold uppercase">{t('services.eyebrow')}</p>
          <h2
            className="mt-2 text-3xl md:text-5xl font-semibold text-[#0b2545]"
            style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
          >
            {t('services.title')}
          </h2>
          <p className="mt-4 max-w-2xl text-[#475569] leading-relaxed">{t('services.body')}</p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesData.map((s) => {
              const Icon = icons[s.id] || Cog;
              const c = s[lang] || s.en || s.it;
              return (
                <div
                  key={s.id}
                  className="group bg-white p-8 border-t-2 border-transparent hover:border-[#c53114] hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-[#fdece7] text-[#c53114] flex items-center justify-center group-hover:bg-[#c53114] group-hover:text-white transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3
                    className="mt-5 text-xl font-semibold text-[#0b2545]"
                    style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
                  >
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default ActivityServicesSection;
